import { Link, Navigate } from "react-router-dom";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import type { AdminActivity, AdminJob, AdminProject } from "../api/types";
import { alertsOn, reportError } from "../lib/alerts";
import { useMe } from "../lib/me";

type TileState = "idle" | "active" | "done" | "stalled" | "error";
interface Tile { state: TileState; label: string; sub: string; }

function transcribeTile(p: AdminProject): Tile {
  if (p.n_audio === 0) return { state: "idle", label: "Transcribe", sub: "no audio" };
  if (p.n_transcribing > 0) return { state: "active", label: "Transcribe", sub: `${p.n_transcribed}/${p.n_audio}` };
  if (p.n_failed_takes > 0) return { state: "error", label: "Transcribe", sub: `${p.n_failed_takes} failed` };
  return { state: "done", label: "Transcribe", sub: `${p.n_transcribed}/${p.n_audio}` };
}
function mapTile(p: AdminProject): Tile {
  if (p.n_transcribed === 0) return { state: "idle", label: "Map", sub: "—" };
  if (p.n_chapters > 0) return { state: "done", label: "Map", sub: `${p.n_chapters} chapters` };
  if (p.proofing_active) return { state: "active", label: "Map", sub: "mapping…" };
  return { state: "stalled", label: "Map", sub: "not mapped" };
}
function proofTile(p: AdminProject): Tile {
  if (p.n_chapters === 0) {
    const st: TileState = p.n_transcribed > 0 ? (p.proofing_active ? "active" : "stalled") : "idle";
    return { state: st, label: "Proof", sub: st === "stalled" ? "stalled" : "—" };
  }
  if (p.n_proofed === p.n_chapters) return { state: "done", label: "Proof", sub: `${p.n_proofed}/${p.n_chapters}` };
  if (p.proofing_active) return { state: "active", label: "Proof", sub: `${p.n_proofed}/${p.n_chapters}` };
  if (p.n_stalled > 0) return { state: "stalled", label: "Proof", sub: `${p.n_proofed}/${p.n_chapters}` };
  return { state: "idle", label: "Proof", sub: `${p.n_proofed}/${p.n_chapters}` };
}

function PipelineTile({ t }: { t: Tile }) {
  const dot = t.state === "active" ? <span className="spinner" style={{ width: 11, height: 11 }} />
    : t.state === "done" ? "✓" : t.state === "stalled" ? "⚠" : t.state === "error" ? "✕" : "•";
  return (
    <div className={`act-tile act-${t.state}`}>
      <div className="act-tile-head"><span className="act-tile-dot">{dot}</span>{t.label}</div>
      <div className="act-tile-sub">{t.sub}</div>
    </div>
  );
}

function ProjectCard({ p }: { p: AdminProject }) {
  return (
    <div className={`act-proj${p.proofing_active ? " live" : ""}`}>
      <div className="act-proj-head">
        <Link to={`/projects/${p.id}`} className="act-proj-name" title={p.name}>{p.name}</Link>
        <span className="act-proj-meta">
          {p.proofing_active && <span className="act-badge live">live</span>}
          {p.n_stalled > 0 && !p.proofing_active && <span className="act-badge stalled">{p.n_stalled} stalled</span>}
          {p.archived && <span className="act-badge muted">archived</span>}
          <span className="muted" title={`owner ${p.owner_id}`}>{p.owner_id.slice(0, 10)}…</span>
        </span>
      </div>
      <div className="act-pipe">
        <PipelineTile t={transcribeTile(p)} />
        <span className="act-arrow">›</span>
        <PipelineTile t={mapTile(p)} />
        <span className="act-arrow">›</span>
        <PipelineTile t={proofTile(p)} />
      </div>
    </div>
  );
}

function JobRow({ j }: { j: AdminJob }) {
  const when = j.ended_at || j.created_at;
  return (
    <div className={`act-job s-${j.status}`}>
      <span className={`act-jstatus s-${j.status}`}>{j.status}</span>
      <span className="act-jproj" title={j.project_id || ""}>{j.project_name || j.project_id || "—"}</span>
      <span className="muted mono" style={{ fontSize: 12 }}>{j.job_type}{j.current_stage ? ` · ${j.current_stage}` : ""}</span>
      {j.error && <span className="act-jerror" title={j.error}>{j.error}</span>}
      <span className="muted act-jtime">{new Date(when).toLocaleTimeString()}</span>
    </div>
  );
}

interface DbCopy {
  id: string; kind: string; status: "running" | "done" | "failed"; attempts: number;
  key: string; size_bytes: number; rows: number; error: string; started_at: string; finished_at: string;
}
interface DbCopies { enabled: boolean; hour_utc: number; copies: DbCopy[] }

const mb = (n: number) => (n >= 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1e3))} KB`);
const at = (iso: string) =>
  iso ? new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";

// Failures first so problems are obvious, then what's running, then what's waiting.
const JOB_ORDER = (status: string) => ({ failed: 0, running: 1, queued: 2 } as Record<string, number>)[status] ?? 3;
const isFiles = (c: DbCopy) => c.kind.startsWith("files");
const size = (n: number) => (n >= 1e9 ? `${(n / 1e9).toFixed(2)} GB` : mb(n));

/** The nightly copies of the database and of new project files (backend
 * core/backup.py, core/file_backup.py), kept off Render. */
function DatabaseCopies() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["admin", "backups"],
    queryFn: () => api<DbCopies>("/api/admin/backups"),
    refetchInterval: (q) => (q.state.data?.copies.some((c) => c.status === "running") ? 3000 : false),
  });
  const now = useMutation({
    mutationFn: () => api<DbCopies>("/api/admin/backups", { method: "POST" }),
    onSuccess: (d) => qc.setQueryData(["admin", "backups"], d),
  });
  const filesNow = useMutation({
    mutationFn: () => api<DbCopies>("/api/admin/backups/files", { method: "POST" }),
    onSuccess: (d) => qc.setQueryData(["admin", "backups"], d),
  });
  if (!data) return null;
  const running = data.copies.some((c) => c.status === "running" && !isFiles(c));
  const filesRunning = data.copies.some((c) => c.status === "running" && isFiles(c));
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="act-copies-head">
        <h3 style={{ margin: 0 }}>Backups</h3>
        {data.enabled && (
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn sm" type="button" disabled={running || now.isPending} onClick={() => now.mutate()}>
              {running ? "Copying…" : "Copy database now"}
            </button>
            <button className="btn sm" type="button" disabled={filesRunning || filesNow.isPending}
              onClick={() => filesNow.mutate()}>
              {filesRunning ? "Copying files…" : "Copy files now"}
            </button>
          </div>
        )}
      </div>
      {!data.enabled ? (
        <div className="muted act-copies-note">
          Not set up on this server: no backup bucket (DEPLOY.md → Backups). Render's own 3-day rollback still applies.
        </div>
      ) : (
        <>
          <div className="muted act-copies-note">
            Every night at {String(data.hour_utc).padStart(2, "0")}:00 UTC: the database, kept 30 days, then any new
            project files (manuscripts, transcripts, proofing results), kept for good. Audio isn't copied here.
          </div>
          {data.copies.length === 0 ? (
            <div className="muted">No copies yet.</div>
          ) : (
            <div className="act-copies">
              {data.copies.map((c) => (
                <div key={c.id} className={`act-copy act-copy-${c.status}`}>
                  <span className="act-copy-when">{at(c.started_at)}</span>
                  <span>
                    {isFiles(c) ? "files" : "database"}, {c.kind.endsWith("manual") ? "by hand" : "nightly"}
                  </span>
                  <span>
                    {c.status === "done"
                      ? isFiles(c)
                        ? `${c.rows.toLocaleString()} new files · ${size(c.size_bytes)}${c.error ? ` (${c.error})` : ""}`
                        : `${mb(c.size_bytes)} · ${c.rows.toLocaleString()} rows`
                      : c.status === "running" ? "copying…"
                      : `failed${c.attempts > 1 ? ` (${c.attempts} tries)` : ""}: ${c.error}`}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      {now.isError && <div className="sw-error">{(now.error as Error).message}</div>}
      {filesNow.isError && <div className="sw-error">{(filesNow.error as Error).message}</div>}
    </div>
  );
}

interface SeedCopy {
  running: boolean;
  started_at: string;
  finished_at: string;
  error?: string;
  projects_total?: number;
  projects_done?: number;
  published?: number;
  removed?: number;
  kept?: number;
  audio_moved?: number;
  audio_bytes?: number;
  failures?: { project_id: string; error: string }[];
  not_in_database?: string[];
  register_files?: number;
  register_bytes?: number;
}
interface FileRegisterState {
  available: boolean;
  switched_on: boolean;
  copy: SeedCopy;
  disk_besides_projects?: { name: string; folder: boolean; bytes: number }[];
}

/** Copies this server's project files into the file register (backend
 * core/register_seed.py) - before the register is switched on, and again
 * straight after to catch up. Hidden where there's no object storage. */
function ProjectFilesCopy() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["admin", "file-register"],
    queryFn: () => api<FileRegisterState>("/api/admin/file-register"),
    refetchInterval: (q) => (q.state.data?.copy.running ? 2000 : false),
  });
  const copy = useMutation({
    mutationFn: () => api<FileRegisterState>("/api/admin/file-register/copy", { method: "POST" }),
    onSuccess: (d) => qc.setQueryData(["admin", "file-register"], d),
  });
  if (!data?.available) return null;
  const c = data.copy;
  const n = (v?: number) => (v ?? 0).toLocaleString();
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="act-copies-head">
        <h3 style={{ margin: 0 }}>Project files</h3>
        <button className="btn sm" type="button" disabled={c.running || copy.isPending} onClick={() => copy.mutate()}>
          {c.running ? "Copying…" : "Copy into the register"}
        </button>
      </div>
      <div className="muted act-copies-note">
        {data.switched_on
          ? "The file register is on. Copying again records anything on this server's disk that isn't in it yet; it never overwrites a version saved elsewhere."
          : "The file register is off: this server reads its own disk. Copy its files in before switching the register on, and again straight after (DEPLOY.md → Project files in the register)."}
      </div>
      {c.running && (
        <div className="muted">Copying… {n(c.projects_done)} of {n(c.projects_total)} projects</div>
      )}
      {!c.running && c.finished_at && !c.error && (
        <div>
          {at(c.finished_at)}: {n(c.projects_total)} projects · {n(c.published)} files added or updated
          · {n(c.removed)} removed · {n(c.kept)} kept as saved elsewhere
          {c.audio_moved ? ` · ${n(c.audio_moved)} audio files moved to storage (${size(c.audio_bytes ?? 0)})` : ""}
          <div className="muted">The register holds {n(c.register_files)} files, {size(c.register_bytes ?? 0)}.</div>
        </div>
      )}
      {c.error && <div className="sw-error">The copy stopped: {c.error}</div>}
      {!!c.failures?.length && (
        <div className="sw-error">
          {c.failures.length} project(s) couldn't be copied (the rest were):
          {c.failures.map((f) => <div key={f.project_id}>{f.project_id}: {f.error}</div>)}
        </div>
      )}
      {!c.running && !!c.not_in_database?.length && (
        <div className="muted">
          {c.not_in_database.length} folder(s) on the disk belong to no project and were left alone.
        </div>
      )}
      {data.disk_besides_projects && (
        <div className="muted">
          {data.disk_besides_projects.length === 0
            ? "Nothing else in this server's data folder."
            : `Also in this server's data folder (not project files): ${data.disk_besides_projects
                .map((e) => `${e.name}${e.folder ? "/" : ""} (${size(e.bytes)})`).join(", ")}.`}
        </div>
      )}
      {copy.isError && <div className="sw-error">{(copy.error as Error).message}</div>}
    </div>
  );
}

/** Sends one made-up error from the server and one from the app, to check
 * both reach Sentry (and that its alert emails arrive). */
function TestErrorButton() {
  const [result, setResult] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const { reporting, problems } = await api<{ reporting: boolean; problems?: string[] }>(
        "/api/admin/test-error", { method: "POST" });
      if (alertsOn()) reportError(new Error("Test error sent from Admin → Activity (nothing is wrong)"), { job: "test" });
      setResult([
        !reporting ? "Server: not set up (no PROOFBOX_SENTRY_DSN)"
          : problems?.length ? `Server: Sentry didn't take it: ${problems.join(" / ")}`
          : "Server: sent",
        alertsOn() ? "App: sent" : "App: not set up (no VITE_SENTRY_DSN in this build)",
      ].join(" · "));
    } catch (e) {
      setResult(`Couldn't send: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="act-test-error">
      <button className="btn sm" type="button" onClick={send} disabled={busy}
        title="Send a made-up error to Sentry from the server and from the app">
        {busy ? "Sending…" : "Send a test error"}
      </button>
      {result && <span className="muted">{result}</span>}
    </div>
  );
}

export function AdminActivity() {
  const { data: me, isLoading: meLoading } = useMe();
  const { data, isLoading } = useQuery({
    queryKey: ["admin-activity"],
    queryFn: () => api<AdminActivity>("/api/admin/activity"),
    refetchInterval: 4000,
    enabled: !!me?.is_superadmin,
  });

  if (meLoading) return <div className="page muted">Loading…</div>;
  if (!me?.is_superadmin) return <Navigate to="/" replace />;

  const projects = data?.projects ?? [];
  const jobs = data?.jobs ?? [];
  const nLive = projects.filter((p) => p.proofing_active).length;
  const nStalled = projects.reduce((n, p) => n + (p.proofing_active ? 0 : p.n_stalled), 0);
  const failedJobs = jobs.filter((j) => j.status === "failed");

  return (
    <div className="page admin-page">
      <div className="page-head">
        <div>
          <h1>Activity</h1>
          <div className="sub">
            {projects.length} project(s) · {nLive} processing
            {nStalled > 0 ? ` · ${nStalled} stalled` : ""}
            {failedJobs.length > 0 ? ` · ${failedJobs.length} failed job(s)` : ""}
            <span className="muted"> · superadmin</span>
          </div>
        </div>
        <TestErrorButton />
      </div>

      {isLoading && !data ? (
        <div className="muted">Loading activity…</div>
      ) : (
        <>
          <DatabaseCopies />
          <ProjectFilesCopy />

          {/* Recent jobs — failures float to the top so problems are obvious. */}
          <div className="card" style={{ marginBottom: 16 }}>
            <h3 style={{ margin: "0 0 8px" }}>Recent jobs</h3>
            {jobs.length === 0 ? (
              <div className="muted">No jobs recorded.</div>
            ) : (
              <div className="act-jobs">
                {[...jobs].sort((a, b) => JOB_ORDER(a.status) - JOB_ORDER(b.status))
                  .map((j) => <JobRow key={j.id} j={j} />)}
              </div>
            )}
            <div className="muted" style={{ fontSize: 11.5, marginTop: 8 }}>
              Full step logs + tracebacks are in the Render service logs (search “[job”).
            </div>
          </div>

          {/* Per-project pipeline board. */}
          <div className="act-board">
            {projects.length === 0
              ? <div className="muted">No projects yet.</div>
              : projects.map((p) => <ProjectCard key={p.id} p={p} />)}
          </div>
        </>
      )}
    </div>
  );
}
